'use client';

import { useTranslations } from 'next-intl';
import { Icon, type IconName } from '@/components/ui/icon';
import { Popover, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/popover';
import { focusRing } from '@/components/ui/focus-ring';
import styles from './sort-control.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

export interface SortOption {
  key: string;
  label: string;
  icon?: IconName;
}

interface SortControlProps {
  value: string;
  onChange: (key: string) => void;
  options: SortOption[];
  /** Accessible name for the menu, e.g. "Sort recipes". */
  ariaLabel: string;
  className?: string;
  /**
   * Effective sort direction. When passed together with `onToggleDir`, an
   * "Order" group (Ascending / Descending) folds into the same menu. Omit both
   * (cookbook toolbars) and no direction section renders.
   */
  dir?: 'asc' | 'desc';
  /** Flip the direction. Only invoked when the user picks the non-active row. */
  onToggleDir?: () => void;
  /** Pre-translated labels for the direction group (used only when `onToggleDir` is set). */
  dirAscLabel?: string;
  dirDescLabel?: string;
  orderLabel?: string;
}

/**
 * SortControl — the toolbar sort-key selector. A house pill trigger
 * ("Sort: {active}", active option's icon + chevron) opening a Popover menu
 * of icon+label rows, the active one marked with a check. When `dir` +
 * `onToggleDir` are supplied, the ascending/descending toggle folds into the
 * same menu as an "Order" group. Composes the library Popover / MenuItem /
 * MenuLabel / MenuSeparator — never redraws a menu row.
 */
export function SortControl({
  value,
  onChange,
  options,
  ariaLabel,
  className,
  dir,
  onToggleDir,
  dirAscLabel,
  dirDescLabel,
  orderLabel,
}: SortControlProps) {
  const t = useTranslations('common');
  const current = options.find((o) => o.key === value) ?? options[0];
  const showDirection = Boolean(onToggleDir);

  const trigger = (
    <button type="button" className={cx(styles.trigger, focusRing, className)}>
      <Icon name={current?.icon ?? 'sort'} size={16} className={styles.leadIcon} />
      <span className={styles.label}>
        <span className={styles.cap}>{t('sortPrefix')}</span>
        <span className={styles.current}>{current?.label ?? ''}</span>
      </span>
      <Icon name="chevron-down" size={14} className={styles.chev} />
    </button>
  );

  return (
    <Popover trigger={trigger} align="end" role="menu" label={ariaLabel}>
      {(close) => (
        <>
          {options.map((o) => {
            const active = o.key === value;
            return (
              <MenuItem
                key={o.key}
                leadingIcon={o.icon}
                trailingIcon={active ? 'check' : undefined}
                aria-current={active || undefined}
                onClick={() => {
                  onChange(o.key);
                  close();
                }}
              >
                {o.label}
              </MenuItem>
            );
          })}
          {showDirection ? (
            <>
              <MenuSeparator />
              {orderLabel ? <MenuLabel>{orderLabel}</MenuLabel> : null}
              <MenuItem
                leadingIcon="arrow-up"
                trailingIcon={dir === 'asc' ? 'check' : undefined}
                aria-current={dir === 'asc' || undefined}
                onClick={() => {
                  if (dir !== 'asc') onToggleDir?.();
                  close();
                }}
              >
                {dirAscLabel}
              </MenuItem>
              <MenuItem
                leadingIcon="arrow-down"
                trailingIcon={dir === 'desc' ? 'check' : undefined}
                aria-current={dir === 'desc' || undefined}
                onClick={() => {
                  if (dir !== 'desc') onToggleDir?.();
                  close();
                }}
              >
                {dirDescLabel}
              </MenuItem>
            </>
          ) : null}
        </>
      )}
    </Popover>
  );
}
